import React, { useState } from 'react';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CreditCardIcon,
  BoltIcon,
  ShieldCheckIcon,
  ClockIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  ArrowRightIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const PayPalSetupGuide: React.FC = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(1);

  const toggleStep = (step: number) => {
    setExpandedStep(expandedStep === step ? null : step);
  };

  const steps = [
    {
      id: 1,
      title: 'Bei PayPal einloggen',
      icon: CreditCardIcon,
      color: 'info',
      content: (
        <div className="space-y-4">
          <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <LightBulbIcon className="h-5 w-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-info-900 dark:text-info-200 mb-1">
                  Tipp für den Start
                </p>
                <p className="text-sm text-info-700 dark:text-info-300">
                  Stellen Sie sicher, dass Sie sich mit dem PayPal-Konto anmelden, das Sie für den Vereinsempfang von Zahlungen verwenden.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Öffnen Sie <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">www.paypal.com</a>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Klicken Sie oben rechts auf "Einloggen"
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Geben Sie Ihre E-Mail-Adresse und Ihr Passwort ein
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                4
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Sie befinden sich jetzt im PayPal-Dashboard
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: 'Verkäufer-Tools öffnen',
      icon: Cog6ToothIcon,
      color: 'primary',
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Klicken Sie oben rechts auf das <strong>Zahnrad-Symbol (⚙️)</strong> für "Einstellungen"
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 border border-gray-300 dark:border-gray-600">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Sie finden es neben "Benachrichtigungen" und "Hilfe"
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Im linken Menü klicken Sie auf <strong>"Verkäufer-Tools"</strong>
                </p>
                <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
                  <p className="text-xs text-primary-700 dark:text-primary-300">
                    Menü-Reihenfolge: Konto → Sicherheit → Daten & Datenschutz → Zahlungen → Benachrichtigungen → <strong>Verkäufer-Tools</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Sie sehen nun verschiedene Optionen. Klicken Sie auf <strong>"Website-Einstellungen"</strong>
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Unter "Online verkaufen" → "Website-Einstellungen": <br />
                    "Steuern Sie Ihre Online-Verkäufe, indem Sie Express-Checkouts aktivieren und Kunden nach der Bezahlung mit PayPal auf Ihre Website zurückleiten."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: 'Automatische Rückleitung aktivieren',
      icon: BoltIcon,
      color: 'success',
      content: (
        <div className="space-y-4">
          <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-success-900 dark:text-success-200 mb-1">
                  Wichtigster Schritt!
                </p>
                <p className="text-sm text-success-700 dark:text-success-300">
                  Hier aktivieren Sie, dass Mitglieder nach der Zahlung automatisch auf unsere Erfolgs-Seite zurückgeleitet werden.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Scrollen Sie zu <strong>"Automatische Rückleitung für Websitezahlungen"</strong>
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Aktivieren Sie die Option <strong>"Ein"</strong>
                  </p>
                  <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
                    <p className="text-xs font-mono text-gray-700 dark:text-gray-300">
                      https://intern.hsv-adlershof.berlin/paypal-success
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ↑ Tragen Sie diese URL in das Feld "Rückleitungs-URL" ein
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: 'Übertragung der Zahlungsdaten (PDT) aktivieren',
      icon: DocumentCheckIcon,
      color: 'warning',
      content: (
        <div className="space-y-4">
          <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-warning-900 dark:text-warning-200 mb-1">
                  Achtung: Notwendig für automatische Verbuchung!
                </p>
                <p className="text-sm text-warning-700 dark:text-warning-300">
                  PDT (Payment Data Transfer) ermöglicht es unserem System, die Zahlungsdetails von PayPal zu empfangen und automatisch die Verzehrkosten als bezahlt zu markieren.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Scrollen Sie weiter nach unten zu <strong>"Übertragung der Zahlungsdaten (optional)"</strong>
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Aktivieren Sie die Option <strong>"Ein"</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Kopieren Sie das <strong>"Identitäts-Token"</strong>
                </p>
                <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
                  <p className="text-xs text-warning-700 dark:text-warning-300 mb-2">
                    <strong>Wichtig:</strong> Bewahren Sie dieses Token sicher auf! Es wird benötigt, wenn Sie PDT in Zukunft verifizieren müssen.
                  </p>
                  <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
                    <p className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                      Beispiel: 6J1w34UfPMjJQayKJM7PgdNjZSUrdcrkq7fb2aJhjru0sQjJpgFZ1KR3hoW
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  Klicken Sie unten auf <strong>"Speichern"</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: 'Fertig! Einrichtung testen',
      icon: CheckCircleIcon,
      color: 'success',
      content: (
        <div className="space-y-4">
          <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-success-900 dark:text-success-200 mb-1">
                  Glückwunsch!
                </p>
                <p className="text-sm text-success-700 dark:text-success-300">
                  Die PayPal-Integration ist nun vollständig eingerichtet. Sie können jetzt Testzahlungen durchführen.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Gehen Sie zu "Meine Verzehrkosten"
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Klicken Sie bei einem unbezahlten Eintrag auf "Mit PayPal bezahlen (Sandbox-Test)"
                </p>
                <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
                  <p className="text-xs text-warning-700 dark:text-warning-300">
                    <strong>Sandbox-Test:</strong> Nutzen Sie zunächst den Sandbox-Button für Testzahlungen ohne echtes Geld. Erst wenn alles funktioniert, nutzen Sie den echten PayPal-Button.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                  Nach erfolgreicher Zahlung werden Sie auf die Erfolgs-Seite weitergeleitet
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Dort sehen Sie alle Zahlungsdetails und die bezahlten Verzehrkosten werden automatisch im System als "bezahlt" markiert.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header - Variante 1: Einfacher Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <CreditCardIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">PayPal-Integration einrichten</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Schritt-für-Schritt-Anleitung zur Einrichtung der automatischen Zahlungsbestätigung</p>
            </div>
          </div>
        </div>
      </header>

      {/* Why is this important? */}
      <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <LightBulbIcon className="h-6 w-6 text-info-600 dark:text-info-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-info-900 dark:text-info-200">
            Warum ist diese Einrichtung notwendig?
          </h2>
        </div>
        <p className="text-sm text-info-700 dark:text-info-300 mb-4">
          Ohne die korrekte PayPal-Einrichtung müssen Zahlungen <strong>manuell</strong> im System verbucht werden. Das bedeutet:
        </p>
        <ul className="space-y-2 text-sm text-info-700 dark:text-info-300">
          <li className="flex items-start gap-2">
            <span className="text-error-600 dark:text-error-400">❌</span>
            <span>Der Kassenwart muss jede PayPal-Zahlung händisch mit den Verzehrkosten abgleichen</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-error-600 dark:text-error-400">❌</span>
            <span>Mitglieder wissen nicht sofort, ob ihre Zahlung angekommen ist</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-error-600 dark:text-error-400">❌</span>
            <span>Doppelzahlungen oder Fehler sind schwerer zu erkennen</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-error-600 dark:text-error-400">❌</span>
            <span>Viel zusätzlicher Verwaltungsaufwand</span>
          </li>
        </ul>
      </div>

      {/* Benefits */}
      <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircleIcon className="h-6 w-6 text-success-600 dark:text-success-400" />
          <h2 className="text-lg font-semibold text-success-900 dark:text-success-200">
            Vorteile der automatischen Integration
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <BoltIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-success-900 dark:text-success-200">Automatische Verbuchung</p>
              <p className="text-xs text-success-700 dark:text-success-300">
                Zahlungen werden sofort als "bezahlt" im System markiert – ohne manuelle Arbeit
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <ClockIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-success-900 dark:text-success-200">Zeitersparnis</p>
              <p className="text-xs text-success-700 dark:text-success-300">
                Keine manuellen Abgleiche mehr notwendig – spart Stunden an Verwaltungsarbeit
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-success-900 dark:text-success-200">Sicher & zuverlässig</p>
              <p className="text-xs text-success-700 dark:text-success-300">
                Transaktions-IDs werden automatisch gespeichert für vollständige Nachvollziehbarkeit
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <BanknotesIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-success-900 dark:text-success-200">Besserer Überblick</p>
              <p className="text-xs text-success-700 dark:text-success-300">
                Mitglieder sehen sofort, für welche Events sie bezahlt haben
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Step-by-step guide */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Schritt-für-Schritt-Anleitung
        </h2>

        <div className="space-y-3">
          {steps.map((step) => {
            const Icon = step.icon;
            const isExpanded = expandedStep === step.id;

            return (
              <div
                key={step.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleStep(step.id)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-${step.color}-100 dark:bg-${step.color}-900/30`}>
                      <Icon className={`h-5 w-5 text-${step.color}-600 dark:text-${step.color}-400`} />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        Schritt {step.id}: {step.title}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-4 bg-white dark:bg-gray-800">
                    {step.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <ExclamationTriangleIcon className="h-6 w-6 text-warning-600 dark:text-warning-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-warning-900 dark:text-warning-200">
            Wichtige Hinweise
          </h2>
        </div>
        <ul className="space-y-2 text-sm text-warning-700 dark:text-warning-300">
          <li className="flex items-start gap-2">
            <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Rückleitungs-URL:</strong> Muss exakt <code className="bg-warning-100 dark:bg-warning-900/40 px-1 rounded">https://intern.hsv-adlershof.berlin/paypal-success</code> lauten
            </span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>PDT aktivieren:</strong> Ohne PDT kann das System die Zahlungsdetails nicht empfangen
            </span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Identitäts-Token:</strong> Bewahren Sie dieses Token sicher auf (z.B. in einem Passwort-Manager)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Sandbox testen:</strong> Testen Sie immer zuerst mit dem Sandbox-Account, bevor Sie echte Zahlungen akzeptieren
            </span>
          </li>
        </ul>
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Zusammenfassung
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Nach Abschluss dieser Einrichtung passiert Folgendes bei jeder PayPal-Zahlung:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-bold">
              1
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Mitglied klickt auf "Mit PayPal bezahlen" bei seinen Verzehrkosten
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-bold">
              2
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Zahlung wird bei PayPal durchgeführt
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-bold">
              3
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              PayPal leitet das Mitglied auf unsere Erfolgs-Seite zurück (mit allen Zahlungsdaten)
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-bold">
              4
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Unser System markiert die Verzehrkosten <strong>automatisch</strong> als bezahlt
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400 flex items-center justify-center text-xs font-bold">
              ✓
            </div>
            <p className="text-sm text-success-700 dark:text-success-300 font-medium">
              Fertig! Keine manuelle Arbeit mehr notwendig.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPalSetupGuide;
